/**
 * TD - Build a bundle (BAB).
 *
 * The cart drawer is pinned to the bottom of the viewport, where it collides with the
 * third-party cookie-consent and accessibility toggles. It claims that space from the
 * shared floating-widget service (assets/td-floating-widgets.js) — the same one the PDP
 * sticky Add to Cart bar uses — so those icons land in the same place on both pages.
 */
if (!customElements.get("td-bundle-builder")) {
  class TdBundleBuilder extends HTMLElement {
    constructor() {
      super();
      
      // Initialize state
      this.supplementaryCart = [];
      this.selectedBundleSize = null;
      this.discountPercentage = 0;
      this.selectedVariantId = null;
      
      // Cache DOM elements
      this.bundleOptions = null;
      this.cartItems = null;
      this.cartCount = null;
      this.cartTotal = null;
      this.addToCartBtn = null;
      
      // Remove icon SVG
      this.removeIconSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none"><circle cx="10.5" cy="10.5" r="10.5" fill="#F22635"/><rect x="5.25" y="8.75" width="10.5" height="3.5" fill="white"/></svg>';
      
      // Bundle cart elements (now part of main cart)
      this.bundleCart = null;
      this.bundleItemsRow = null;
      this.bundleTotal = null;
      this.bundleCount = null;
      this.bundleAddBtn = null;
      
      // Desktop heading elements
      this.notFilledHeading = null;
      this.filledHeading = null;
      this.drawerToggle = null;
      
      // Scroll detection
      this.scrollThreshold = 10; // pixels from bottom to hide cart
      this.isCartVisible = true;

      // Bottom space claimed from the shared floating-widget service.
      this.widgetClaimId = 'td-bundle-cart';
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      
      // Bind methods
      this.handleBundleOptionChange = this.handleBundleOptionChange.bind(this);
      this.handleAddProduct = this.handleAddProduct.bind(this);
      this.handleRemoveProduct = this.handleRemoveProduct.bind(this);
      this.handleAddToCart = this.handleAddToCart.bind(this);
      this.toggleBundleCart = this.toggleBundleCart.bind(this);
    }

    connectedCallback() {
      this.initializeElements();
      this.loadPersistedState();
      this.setupEventListeners();
      this.initializeDefaultSelection();
      this.initializeCartState();
      this.updateCartDisplay();
      this.syncWidgetClaim();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleViewportChange);
      if (this.drawerTransitionEnd && this.bundleCart) {
        this.bundleCart.removeEventListener('transitionend', this.drawerTransitionEnd);
      }
      // Leave no widgets stranded off-position if the section is torn down.
      if (window.TDFloatingWidgets) window.TDFloatingWidgets.release(this.widgetClaimId);
    }

    /**
     * Publish the drawer's current height to the shared floating-widget service so the
     * consent/accessibility toggles sit clear of it. Height is 0 while the drawer is
     * scrolled out (handleScroll), which releases the claim and settles them back.
     */
    syncWidgetClaim() {
      if (!window.TDFloatingWidgets || !this.bundleCart) return;
      const height = this.isCartVisible
        ? Math.ceil(this.bundleCart.getBoundingClientRect().height)
        : 0;
      window.TDFloatingWidgets.claim(this.widgetClaimId, height, { element: this.bundleCart });
    }

    /** Re-measure after a viewport change — the drawer height is layout-dependent. */
    handleViewportChange() {
      this.syncWidgetClaim();
    }

    /**
     * Initialize DOM element references
     */
    initializeElements() {
      this.bundleOptions = this.querySelectorAll('custom-box-variant');
      this.cartItems = this.querySelector('.td-bundle-builder__cart-items');
      this.cartCount = this.querySelector('.td-bundle-builder__count-text');
      this.cartTotal = this.querySelector('.td-bundle-builder__total-text');
      this.addToCartBtn = this.querySelector('.td-bundle-builder__add-to-cart-btn');
      
      // Bundle cart elements
      this.bundleCart = this.querySelector('[data-td-bundle-cart]');
      this.bundleItemsRow = this.querySelector('.td-bundle-builder__cart-items-row');
      this.bundleTotal = this.querySelector('.td-bundle-builder__cart-total-amount');
      this.bundleCount = this.querySelector('.td-bundle-builder__cart-count-text');
      this.bundleAddBtn = this.querySelector('.td-bundle-builder__cart-add-btn');
      this.drawerToggle = this.querySelector('.td-bundle-builder__drawer-toggle');
      
      // Desktop heading elements
      this.notFilledHeading = this.querySelector('.td-bundle-builder__cart-heading--not-filled');
      this.filledHeading = this.querySelector('.td-bundle-builder__cart-heading--filled');
    }

    /**
     * Set up event listeners for user interactions
     */
    setupEventListeners() {
      // Bundle size option listeners
      this.bundleOptions.forEach(option => {
        option.addEventListener('click', this.handleBundleOptionChange);
      });

      // Use event delegation for add product buttons
      this.addEventListener('click', (event) => {
        if (event.target.closest('.td-bundle-builder__add-btn') && !event.target.closest('.td-bundle-builder__quantity-btn')) {
          this.handleAddProduct(event);
        }
      });

      // Add to cart button listener
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', this.handleAddToCart);
      }
      
      // Bundle cart listeners
      if (this.bundleAddBtn) {
        this.bundleAddBtn.addEventListener('click', this.handleAddToCart);
      }
      
      if (this.drawerToggle) {
        this.drawerToggle.addEventListener('click', this.toggleBundleCart);
      }
      
      // Add scroll listener for cart visibility
      window.addEventListener('scroll', this.handleScroll);

      // The drawer height drives how far the floating widgets lift, so re-measure on
      // viewport change and once the expand/collapse transition has actually landed.
      window.addEventListener('resize', this.handleViewportChange);
      if (this.bundleCart) {
        this.drawerTransitionEnd = (event) => {
          if (event.target === this.bundleCart || this.bundleCart.contains(event.target)) {
            this.syncWidgetClaim();
          }
        };
        this.bundleCart.addEventListener('transitionend', this.drawerTransitionEnd);
      }
    }

    /**
     * Handle bundle size selection
     */
    handleBundleOptionChange(event) {
      const selectedOption = event.currentTarget;
      const variantId = selectedOption.dataset.variantId;
      const choices = parseInt(selectedOption.dataset.choices);
      const discount = parseInt(selectedOption.dataset.variantDiscount);
      
      // Update URL with variant ID
      this.updateURLWithVariant(variantId);
      
      // Update selected state
      this.updateVariantSelection(selectedOption);
      
      // Update bundle state
      this.selectedBundleSize = choices;
      this.selectedVariantId = variantId;
      this.discountPercentage = discount;
      
      // Remove excess products if new bundle size is smaller
      this.removeExcessProducts(choices);
      
      // Save state and update cart display
      this.saveState();
      this.updateCartDisplay();
    }

    /**
     * Remove excess products when switching to a smaller bundle size
     */
    removeExcessProducts(newBundleSize) {
      if (this.supplementaryCart.length <= newBundleSize) {
        return; // No excess products to remove
      }
      
      // Calculate how many products to remove
      const excessCount = this.supplementaryCart.length - newBundleSize;
      
      // Remove the most recently added products (last items in the array)
      this.supplementaryCart.splice(-excessCount);
      
      // Update all add button states after removing products
      const addButtons = this.querySelectorAll('.td-bundle-builder__add-btn');
      addButtons.forEach(button => {
        this.updateAddButtonState(button);
      });
    }

    /**
     * Initialize default variant selection
     */
    initializeDefaultSelection() {
      // Only initialize default if no variant is already selected
      if (!this.selectedVariantId) {
        const firstOption = this.bundleOptions[0];
        if (firstOption) {
          const variantId = firstOption.dataset.variantId;
          const choices = parseInt(firstOption.dataset.choices);
          const discount = parseInt(firstOption.dataset.variantDiscount);
          
          // Set initial state
          this.selectedBundleSize = choices;
          this.selectedVariantId = variantId;
          this.discountPercentage = discount;
          
          // Update URL with default variant
          this.updateURLWithVariant(variantId);
          
          // Save state if no persisted state was loaded
          if (this.supplementaryCart.length === 0) {
            this.saveState();
          }
        }
      } else {
        // If we have a selected variant, update the URL with it
        this.updateURLWithVariant(this.selectedVariantId);
      }
    }

    /**
     * Initialize cart state
     */
    initializeCartState() {
      if (!this.bundleCart) return;
      
      // Start with collapsed state by default
      this.bundleCart.classList.add('collapsed');
      this.bundleCart.classList.remove('expanded');
      
      // Set initial content state based on cart items
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const isComplete = totalQuantity === maxItems && maxItems > 0;
      
      if (totalQuantity === 0) {
        this.bundleCart.classList.add('empty');
        this.bundleCart.classList.remove('partial', 'full');
      } else if (isComplete) {
        this.bundleCart.classList.add('full');
        this.bundleCart.classList.remove('empty', 'partial');
      } else {
        this.bundleCart.classList.add('partial');
        this.bundleCart.classList.remove('empty', 'full');
      }
    }

    /**
     * Update URL with selected variant ID
     */
    updateURLWithVariant(variantId) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', currentUrl.toString());
    }

    /**
     * Update variant selection visual state
     */
    updateVariantSelection(selectedOption) {
      // Remove selected state from all options
      this.bundleOptions.forEach(option => {
        option.dataset.selected = 'false';
        option.classList.remove('selected');
      });
      
      // Add selected state to chosen option
      selectedOption.dataset.selected = 'true';
      selectedOption.classList.add('selected');
    }

    /**
     * Handle adding a product to the supplementary cart
     */
    handleAddProduct(event) {
      const button = event.target.closest('.td-bundle-builder__add-btn');
      if (!button) return;
      
      // Check if bundle size is selected
      if (!this.selectedBundleSize) {
        alert('Please select a bundle size first!');
        return;
      }
      
      // Check if cart is full
      if (this.supplementaryCart.length >= this.selectedBundleSize) {
        return; // Don't add, but don't show alert
      }
      
      // Get product data and add to cart as separate item
      const productElement = button.closest('.td-bundle-builder__product');
      const productData = this.getProductData(productElement);
      productData.uniqueId = Date.now() + Math.random();
      this.supplementaryCart.push(productData);
      
      // Update button state
      this.updateAddButtonState(button);
      
      // Save state and update cart display
      this.saveState();
      this.updateCartDisplay();
    }

    /**
     * Handle removing a product from the supplementary cart
     */
    handleRemoveProduct(event) {
      const removeButton = event.currentTarget;
      const uniqueId = removeButton.dataset.uniqueId;
      
      // Remove specific item from supplementary cart
      const productIndex = this.supplementaryCart.findIndex(item => String(item.uniqueId) === uniqueId);
      if (productIndex !== -1) {
        const removedProduct = this.supplementaryCart[productIndex];
        this.supplementaryCart.splice(productIndex, 1);
        
        // Update add button state for the product type
        const addButton = this.querySelector(`[data-product-id="${removedProduct.productId}"]`);
        if (addButton) {
          this.updateQuantityDisplay(removedProduct.productId);
        }
      }
      
      // Save state and update cart display
      this.saveState();
      this.updateCartDisplay();
    }

    /**
     * Handle adding the entire bundle to the real cart
     */
    async handleAddToCart() {
      if (this.supplementaryCart.length === 0) {
        alert('Please add products to your bundle first!');
        return;
      }
      
      const totalQuantity = this.supplementaryCart.length;
      if (totalQuantity < this.selectedBundleSize) {
        alert(`Please add ${this.selectedBundleSize} products to complete your bundle.`);
        return;
      }
      
      try {
        // Generate unique bundle ID
        const bundleId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          function (c) {
            var r = (Math.random() * 16) | 0,
              v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          }
        );
        
        // Get all child product IDs (variant IDs)
        const childProductIds = this.supplementaryCart.map(product => product.id);
        
        // Get the parent bundle product ID (selected variant)
        const parentProductId = this.selectedVariantId;
        
        // Prepare cart items with proper bundle properties
        const cartItems = [];
        
        // Add child products first
        childProductIds.forEach((productId, index) => {
          const childItem = {
            id: productId,
            quantity: 1,
            properties: {
              _bundle_group_id: bundleId,
              _boxItem: childProductIds,
              _index: index,
              _bundleParentId: bundleId
            }
          };
          cartItems.push(childItem);
        });
        
        // Add parent bundle product
        const parentItem = {
          id: parentProductId,
          quantity: 1,
          properties: {
            _bundle_group_id: bundleId,
            _boxItem: childProductIds,
            _index: childProductIds.length,
            _bundleId: bundleId,
            _bundleDiscountAmount: this.discountPercentage
          }
        };
        cartItems.push(parentItem);
        
        // Add to Shopify cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: cartItems
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          
          // Clear supplementary cart and persisted state
          this.supplementaryCart = [];
          this.clearPersistedState();
          
          // Reset all add buttons
          const addButtons = this.querySelectorAll('.td-bundle-builder__add-btn');
          addButtons.forEach(button => {
            this.updateAddButtonState(button);
          });
          
          // Update cart display after resetting everything
          this.updateCartDisplay();
          
          // Dispatch the proper CartAddEvent that the theme expects
          const cartAddEvent = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: this.id || 'td-bundle-builder',
              data: {
                source: 'td-bundle-builder',
                itemCount: 0,
                sections: result.sections || {}
              }
            }
          });
          document.dispatchEvent(cartAddEvent);
          
          // Also dispatch the legacy cart:updated event for compatibility
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Manually open the cart drawer
          try {
            this.openCart();
          } catch (error) {
            console.error('Error opening cart drawer:', error);
            alert('There was an error opening the cart drawer. Please try again.');
          }
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to add bundle to cart: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        alert('There was an error adding your bundle to the cart. Please try again.');
      }
    }

    /**
     * Extract product data from DOM element
     */
    getProductData(productElement) {
      const productId = productElement.dataset.productId;
      const variantId = productElement.dataset.variantId;
      const productHandle = productElement.dataset.productHandle;
      const title = productElement.querySelector('.td-bundle-builder__product-title').textContent.trim();
      const priceElement = productElement.querySelector('.td-bundle-builder__price, .td-bundle-builder__price--sale');
      const price = this.extractPrice(priceElement.textContent);
      
      // Look for image element - try multiple selectors
      const imageElement = productElement.querySelector('.td-bundle-builder__image, .td-img, img');
      const imageSrc = imageElement ? imageElement.src : '';
      
      return {
        id: variantId,
        productId: productId,
        handle: productHandle,
        title: title,
        price: price,
        image: imageSrc
      };
    }

    /**
     * Extract numeric price from price string
     */
    extractPrice(priceString) {
      const priceMatch = priceString.match(/[\d,]+\.?\d*/);
      return priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : 0;
    }

    /**
     * Update add button state (added/not added)
     */
    updateAddButtonState(button) {
      const variantId = button.dataset.variantId;
      const productsInCart = this.supplementaryCart.filter(item => item.id === variantId);
      const quantity = productsInCart.length;
      
      if (quantity > 0) {
        // Transform to quantity selector
        this.createQuantitySelector(button, quantity);
      } else {
        // Reset to add button
        this.resetToAddButton(button);
      }
    }

    /**
     * Create quantity selector for added product
     */
    createQuantitySelector(button, quantity) {
      const productId = button.dataset.productId;
      
      // Check if we're at the bundle limit
      const isAtLimit = this.supplementaryCart.length >= this.selectedBundleSize && this.selectedBundleSize > 0;
      
      // Generate quantity selector HTML inline
      button.innerHTML = `
        <div class="td-bundle-builder__quantity-selector">
          <button class="td-bundle-builder__quantity-btn td-bundle-builder__quantity-minus" data-product-id="${productId}">-</button>
          <span class="td-bundle-builder__quantity-display">${quantity}</span>
          <button class="td-bundle-builder__quantity-btn td-bundle-builder__quantity-plus" data-product-id="${productId}" ${isAtLimit ? 'disabled' : ''}>+</button>
        </div>
      `;
      button.classList.add('quantity-mode');
      button.classList.remove('added');
      
      // Add event listeners to quantity buttons
      const minusBtn = button.querySelector('.td-bundle-builder__quantity-minus');
      const plusBtn = button.querySelector('.td-bundle-builder__quantity-plus');
      
      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleQuantityChange(productId, -1);
      });
      
      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleAddAnotherProduct(productId);
      });
      
      // Apply disabled styling if at limit
      if (isAtLimit && plusBtn) {
        plusBtn.style.opacity = '0.5';
        plusBtn.style.cursor = 'not-allowed';
      }
    }

    /**
     * Reset button back to add state
     */
    resetToAddButton(button) {
      // Reset to the original add button structure
      button.innerHTML = `
        <span class="td-bundle-builder__add-icon">+</span>
      `;
      button.classList.remove('quantity-mode', 'added');
    }

    /**
     * Handle adding another product of the same type
     */
    handleAddAnotherProduct(productId) {
      // Check if bundle size limit is reached
      const totalQuantity = this.supplementaryCart.length;
      if (totalQuantity >= this.selectedBundleSize) {
        return; // Don't add, but don't show alert
      }
      
      // Find the product element to get data
      const productElement = this.querySelector(`[data-product-id="${productId}"]`).closest('.td-bundle-builder__product');
      const productData = this.getProductData(productElement);
      productData.uniqueId = Date.now() + Math.random();
      this.supplementaryCart.push(productData);
      
      // Update quantity display
      this.updateQuantityDisplay(productId);
      
      // Save state and update cart display
      this.saveState();
      this.updateCartDisplay();
    }

    /**
     * Handle quantity changes (only for removing)
     */
    handleQuantityChange(productId, change) {
      // Only handle removal (negative changes)
      if (change >= 0) return;
      
      // Find the last occurrence of this product (most recently added)
      const productIndex = this.supplementaryCart.map((item, index) => ({ item, index }))
        .filter(({ item }) => item.productId === productId)
        .pop()?.index;
      
      if (productIndex === undefined) return;
      
      // Remove the specific product from cart
      this.supplementaryCart.splice(productIndex, 1);
      
      // Update quantity display
      this.updateQuantityDisplay(productId);
      
      // Save state and update cart display
      this.saveState();
      this.updateCartDisplay();
    }

    /**
     * Update quantity display for a product
     */
    updateQuantityDisplay(productId) {
      const productElement = this.querySelector(`[data-product-id="${productId}"]`);
      if (!productElement) return;
      
      const addButton = productElement.querySelector('.td-bundle-builder__add-btn');
      if (!addButton) return;
      
      const variantId = addButton.dataset.variantId;
      const productsInCart = this.supplementaryCart.filter(item => item.id === variantId);
      const quantity = productsInCart.length;
      
      if (quantity === 0) {
        // Reset button to add state
        this.updateAddButtonState(addButton);
      } else {
        // Update quantity display
        const quantityDisplay = addButton.querySelector('.td-bundle-builder__quantity-display');
        if (quantityDisplay) {
          quantityDisplay.textContent = quantity;
        }
      }
    }

    /**
     * Update the cart summary display
     */
    updateCartDisplay() {
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const subtotal = this.supplementaryCart.reduce((sum, item) => sum + item.price, 0);
      const discount = subtotal * (this.discountPercentage / 100);
      const total = subtotal - discount;
      
      // Update cart count
      if (this.cartCount) {
        this.cartCount.textContent = `${totalQuantity}/${maxItems} ADDED TO BOX`;
      }
      
      // Update total price
      if (this.cartTotal) {
        this.cartTotal.textContent = `TOTAL: $${total.toFixed(2)}`;
      }
      
      // Update cart items display
      this.updateCartItemsDisplay();
      
      // Update add to cart button state
      this.updateAddToCartButtonState();
      
      // Update all button states based on bundle limit
      this.updateAllButtonStates();
      
      // Update bundle cart display
      this.updateBundleCartDisplay();
      
      // Update desktop headings
      this.updateDesktopHeadings();
    }

    /**
     * Update desktop headings based on cart state and drawer state
     */
    updateDesktopHeadings() {
      if (!this.notFilledHeading || !this.filledHeading) return;
      
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const isCartFilled = totalQuantity === maxItems && maxItems > 0;
      const isDrawerExpanded = this.bundleCart && this.bundleCart.classList.contains('expanded');
      
      // Show filled heading only when drawer is expanded AND cart is filled
      if (isDrawerExpanded && isCartFilled) {
        this.notFilledHeading.style.display = 'none';
        this.filledHeading.style.display = 'block';
      } else {
        // Show not-filled heading in all other cases
        this.notFilledHeading.style.display = 'block';
        this.filledHeading.style.display = 'none';
      }
    }

    /**
     * Handle scroll events to show/hide cart based on position
     */
    handleScroll() {
      if (!this.bundleCart) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
      
      // Hide cart when close to bottom of page
      if (distanceFromBottom < this.scrollThreshold) {
        if (this.isCartVisible) {
          this.bundleCart.style.opacity = '0';
          this.bundleCart.style.pointerEvents = 'none';
          this.isCartVisible = false;
          this.syncWidgetClaim();
        }
      } else {
        if (!this.isCartVisible) {
          this.bundleCart.style.opacity = '1';
          this.bundleCart.style.pointerEvents = 'auto';
          this.isCartVisible = true;
          this.syncWidgetClaim();
        }
      }
    }

    /**
     * Update the cart items visual display
     */
    updateCartItemsDisplay() {
      if (!this.cartItems) return;
      
      // Clear existing items
      this.cartItems.innerHTML = '';
      
      // Add each item
      this.supplementaryCart.forEach(product => {
        const itemElement = this.createCartItemElement(product);
        this.cartItems.appendChild(itemElement);
      });
    }

    /**
     * Create a cart item element
     */
    createCartItemElement(product) {
      const itemDiv = document.createElement('div');
      itemDiv.className = 'td-bundle-builder__cart-item';
      
      const image = document.createElement('img');
      image.className = 'td-bundle-builder__cart-item-image';
      image.src = product.image;
      image.alt = product.title;
      
      const title = document.createElement('span');
      title.className = 'td-bundle-builder__cart-item-title';
      title.textContent = product.title;
      
      const removeButton = document.createElement('button');
      removeButton.className = 'td-bundle-builder__remove-btn';
      removeButton.innerHTML = this.removeIconSvg;
      removeButton.dataset.uniqueId = product.uniqueId;
      removeButton.addEventListener('click', this.handleRemoveProduct);
      
      itemDiv.appendChild(image);
      itemDiv.appendChild(title);
      itemDiv.appendChild(removeButton);
      
      return itemDiv;
    }

    /**
     * Update the add to cart button state
     */
    updateAddToCartButtonState() {
      if (!this.addToCartBtn) return;
      
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const isComplete = totalQuantity === maxItems && maxItems > 0;
      
      this.addToCartBtn.disabled = !isComplete;
      
      if (isComplete) {
        this.addToCartBtn.textContent = 'ADD TO CART';
      } else if (maxItems > 0) {
        this.addToCartBtn.textContent = `ADD ${maxItems - totalQuantity} MORE`;
      } else {
        this.addToCartBtn.textContent = 'SELECT BUNDLE SIZE';
      }
    }

    /**
     * Update all button states based on bundle limit
     */
    updateAllButtonStates() {
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const isAtLimit = totalQuantity >= maxItems && maxItems > 0;
      
      // Update all add buttons
      const addButtons = this.querySelectorAll('.td-bundle-builder__add-btn');
      addButtons.forEach(button => {
        if (isAtLimit && !button.classList.contains('quantity-mode')) {
          // Disable add buttons when at limit
          button.disabled = true;
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
        } else if (!button.classList.contains('quantity-mode')) {
          // Enable add buttons when not at limit
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
        }
      });
      
      // Update all plus buttons in quantity selectors
      const plusButtons = this.querySelectorAll('.td-bundle-builder__quantity-plus');
      plusButtons.forEach(button => {
        if (isAtLimit) {
          // Disable plus buttons when at limit
          button.disabled = true;
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
        } else {
          // Enable plus buttons when not at limit
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
        }
      });
    }

    /**
     * Save current state to localStorage
     */
    saveState() {
      const state = {
        supplementaryCart: this.supplementaryCart,
        selectedBundleSize: this.selectedBundleSize,
        selectedVariantId: this.selectedVariantId,
        discountPercentage: this.discountPercentage,
        timestamp: Date.now()
      };
      
      try {
        localStorage.setItem('td-bundle-builder-state', JSON.stringify(state));
      } catch (error) {
        console.warn('Failed to save bundle builder state to localStorage:', error);
      }
    }

    /**
     * Load persisted state from localStorage
     */
    loadPersistedState() {
      try {
        const savedState = localStorage.getItem('td-bundle-builder-state');
        if (savedState) {
          const state = JSON.parse(savedState);
          
          // Check if state is not too old (24 hours)
          const isExpired = Date.now() - state.timestamp > 24 * 60 * 60 * 1000;
          if (isExpired) {
            localStorage.removeItem('td-bundle-builder-state');
            return;
          }
          
          // Restore state
          this.supplementaryCart = state.supplementaryCart || [];
          this.selectedBundleSize = state.selectedBundleSize;
          this.selectedVariantId = state.selectedVariantId;
          this.discountPercentage = state.discountPercentage || 0;
          
          // Restore variant selection if it exists
          if (this.selectedVariantId) {
            this.restoreVariantSelection();
          }
          
          // Restore add button states for products in cart
          this.restoreAddButtonStates();
        }
      } catch (error) {
        console.warn('Failed to load bundle builder state from localStorage:', error);
        localStorage.removeItem('td-bundle-builder-state');
      }
    }

    /**
     * Restore variant selection visual state
     */
    restoreVariantSelection() {
      if (!this.selectedVariantId || !this.bundleOptions) return;
      
      const targetOption = Array.from(this.bundleOptions).find(option => 
        option.dataset.variantId === this.selectedVariantId
      );
      
      if (targetOption) {
        this.updateVariantSelection(targetOption);
      }
    }

    /**
     * Restore add button states for products in cart
     */
    restoreAddButtonStates() {
      if (!this.supplementaryCart.length) return;
      
      // Group products by variant ID to count quantities
      const variantCounts = {};
      this.supplementaryCart.forEach(product => {
        variantCounts[product.id] = (variantCounts[product.id] || 0) + 1;
      });
      
      // Update each add button based on variant count in cart
      Object.keys(variantCounts).forEach(variantId => {
        // Find the product element that has this variant ID
        const productElement = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (productElement) {
          const addButton = productElement.querySelector('.td-bundle-builder__add-btn');
          if (addButton) {
            const quantity = variantCounts[variantId];
            if (quantity > 0) {
              this.createQuantitySelector(addButton, quantity);
            }
          }
        }
      });
    }

    /**
     * Clear persisted state
     */
    clearPersistedState() {
      try {
        localStorage.removeItem('td-bundle-builder-state');
      } catch (error) {
        console.warn('Failed to clear bundle builder state from localStorage:', error);
      }
    }

    /**
     * Open the cart drawer/modal
     */
    openCart() {
      const cartDrawerComponent = document.querySelector('cart-drawer-component');
      if (!cartDrawerComponent) {
        throw new Error('Cart drawer component not found');
      }
      
      if (typeof cartDrawerComponent.open !== 'function') {
        throw new Error('Cart drawer component does not have an open method');
      }
      
      cartDrawerComponent.open();
    }

    /**
     * Toggle bundle cart expanded/collapsed state
     */
    toggleBundleCart() {
      if (!this.bundleCart) return;
      
      const isExpanded = this.bundleCart.classList.contains('expanded');
      if (isExpanded) {
        this.bundleCart.classList.remove('expanded');
        this.bundleCart.classList.add('collapsed');
      } else {
        this.bundleCart.classList.remove('collapsed');
        this.bundleCart.classList.add('expanded');
      }
      
      // Update desktop headings after toggle
      this.updateDesktopHeadings();

      // Height changed; lift the floating widgets to match. transitionend refines this
      // once the animation lands, but claim immediately so nothing overlaps mid-slide.
      this.syncWidgetClaim();
    }

    /**
     * Update bundle cart display
     */
    updateBundleCartDisplay() {
      if (!this.bundleCart || !this.bundleItemsRow || !this.bundleTotal || !this.bundleCount || !this.bundleAddBtn) return;
      
      const totalQuantity = this.supplementaryCart.length;
      const maxItems = this.selectedBundleSize || 0;
      const subtotal = this.supplementaryCart.reduce((sum, item) => sum + item.price, 0);
      const discount = subtotal * (this.discountPercentage / 100);
      const total = subtotal - discount;
      const isComplete = totalQuantity === maxItems && maxItems > 0;
      
      // Update total and count
      this.bundleTotal.textContent = `$${total.toFixed(2)}`;
      this.bundleCount.textContent = `${totalQuantity}/${maxItems} ADDED TO BOX`;
      
      // Update button state
      this.bundleAddBtn.disabled = !isComplete;
      
      // Preserve expanded/collapsed state
      const isExpanded = this.bundleCart.classList.contains('expanded');
      const isCollapsed = this.bundleCart.classList.contains('collapsed');
      
      // Update cart content state (independent of expanded/collapsed)
      if (totalQuantity === 0) {
        this.bundleCart.classList.remove('partial', 'full');
        this.bundleCart.classList.add('empty');
      } else if (isComplete) {
        this.bundleCart.classList.remove('empty', 'partial');
        this.bundleCart.classList.add('full');
      } else {
        this.bundleCart.classList.remove('empty', 'full');
        this.bundleCart.classList.add('partial');
      }
      
      // Restore expanded/collapsed state if it was set
      if (isExpanded) {
        this.bundleCart.classList.add('expanded');
        this.bundleCart.classList.remove('collapsed');
      } else if (isCollapsed) {
        this.bundleCart.classList.add('collapsed');
        this.bundleCart.classList.remove('expanded');
      }
      
      // Update items row
      this.toggleBundleItemsRow();

      // The items row changes the drawer height, so re-measure the widget lift.
      this.syncWidgetClaim();
    }

    /**
     * Update bundle items row
     */
    toggleBundleItemsRow() {
      if (!this.bundleItemsRow) return;
      
      // Clear existing items
      this.bundleItemsRow.innerHTML = '';
      
      const maxItems = this.selectedBundleSize || 0;
      
      // Add filled items
      this.supplementaryCart.forEach((product, index) => {
        const itemElement = this.createBundleItemElement(product, index + 1);
        this.bundleItemsRow.appendChild(itemElement);
      });
      
      // Add empty slots
      for (let i = this.supplementaryCart.length + 1; i <= maxItems; i++) {
        const emptyElement = this.createBundleEmptyElement(i);
        this.bundleItemsRow.appendChild(emptyElement);
      }
    }

    /**
     * Create bundle item element
     */
    createBundleItemElement(product, position) {
      const itemDiv = document.createElement('div');
      itemDiv.className = 'td-bundle-builder__cart-item';
      
      const image = document.createElement('img');
      image.className = 'td-bundle-builder__cart-item-image';
      image.src = product.image;
      image.alt = product.title;
      
      const removeButton = document.createElement('button');
      removeButton.className = 'td-bundle-builder__cart-item-remove';
      removeButton.innerHTML = this.removeIconSvg;
      removeButton.dataset.uniqueId = product.uniqueId;
      removeButton.addEventListener('click', this.handleRemoveProduct);
      
      itemDiv.appendChild(image);
      itemDiv.appendChild(removeButton);
      
      return itemDiv;
    }

    /**
     * Create bundle empty element
     */
    createBundleEmptyElement(position) {
      const emptyDiv = document.createElement('div');
      emptyDiv.className = 'td-bundle-builder__cart-item-empty';
      emptyDiv.textContent = position.toString();
      return emptyDiv;
    }


  }

  customElements.define("td-bundle-builder", TdBundleBuilder);
}
